/**
 * EffectsPanel - Interface pour les effets (Delay, Reverb, Compressor)
 * Utilise ThemeManager pour les composants UI
 */

import { themeManager } from '../themes/ThemeManager.js';

export class EffectsPanel {
    constructor(audioEngine, store) {
        this.audioEngine = audioEngine;
        this.delay = audioEngine.getDelay();
        this.reverb = audioEngine.getReverb();
        this.chorus = audioEngine.getChorus();
        this.phaser = audioEngine.getPhaser();
        this.flanger = audioEngine.getFlanger();
        this.bitcrusher = audioEngine.getBitcrusher();
        this.tremolo = audioEngine.getTremolo();
        this.distortion = audioEngine.getDistortion();
        this.eq = audioEngine.getEQ();
        this.compressor = audioEngine.getCompressor();
        this.store = store;
        this.panelId = 'effects';

        this.components = {
            knobs: {},
            selects: {}
        };

        // FX activity indicators
        this.effectIndicators = {};
        this.unregisterEffectActivity = null;

        // GR meter for compressor (throttled in low-power mode)
        this.grAnimationId = null;
        this.grMeter = null;
        this.grEnabled = !this.audioEngine?.performance?.lowPower;
        this.grIntervalMs = this.audioEngine?.performance?.lowPower ? 1000 / 12 : 1000 / 30;
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();

        container.innerHTML = `
            <div class="machine effects ${isCollapsed ? 'collapsed' : ''}" id="effects_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="effects_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    EFFECTS <span class="brand">Send FX + Master</span>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="effects_content">
                <div class="effects-grid">
                    <div class="effects-section">
                        <div class="effect-title">DELAY <span class="fx-indicator" data-effect="delay"></span></div>
                        <div class="controls vertical" id="delay_controls"></div>
                    </div>

                    <div class="effects-section">
                        <div class="effect-title">REVERB <span class="fx-indicator" data-effect="reverb"></span></div>
                        <div class="controls vertical" id="reverb_controls"></div>
                    </div>

                    <div class="effects-section">
                        <div class="effect-title chorus-title">CHORUS <span class="fx-indicator" data-effect="chorus"></span></div>
                        <div class="controls vertical" id="chorus_controls"></div>
                    </div>

                    <div class="effects-section">
                        <div class="effect-title phaser-title">PHASER <span class="fx-indicator" data-effect="phaser"></span></div>
                        <div class="controls vertical" id="phaser_controls"></div>
                    </div>

                    <div class="effects-section">
                        <div class="effect-title flanger-title">FLANGER <span class="fx-indicator" data-effect="flanger"></span></div>
                        <div class="controls vertical" id="flanger_controls"></div>
                    </div>

                    <div class="effects-section">
                        <div class="effect-title crusher-title">BITCRUSH <span class="fx-indicator" data-effect="crusher"></span></div>
                        <div class="controls vertical" id="crusher_controls"></div>
                    </div>

                    <div class="effects-section">
                        <div class="effect-title tremolo-title">TREMOLO <span class="fx-indicator" data-effect="tremolo"></span></div>
                        <div class="controls vertical" id="tremolo_controls"></div>
                    </div>

                    <div class="effects-section">
                        <div class="effect-title distortion-title">DISTORT <span class="fx-indicator" data-effect="distortion"></span></div>
                        <div class="controls vertical" id="distortion_controls"></div>
                    </div>

                    <div class="effects-section">
                        <div class="effect-title eq-title">MASTER EQ</div>
                        <div class="controls vertical" id="eq_controls"></div>
                    </div>

                    <div class="effects-section">
                        <div class="effect-title compressor-title">COMPRESS</div>
                        <div class="controls vertical" id="comp_controls"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();

        if (!isCollapsed) {
            this.createDelayControls();
            this.createReverbControls();
            this.createChorusControls();
            this.createPhaserControls();
            this.createFlangerControls();
            this.createCrusherControls();
            this.createTremoloControls();
            this.createDistortionControls();
            this.createEQControls();
            this.createCompressorControls();
            this.cacheEffectIndicators();
            this.registerEffectActivityCallback();
            if (this.grEnabled) {
                this.startGRMeter();
            }
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('effects_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    /**
     * Cache FX indicator DOM elements for efficient updates
     */
    cacheEffectIndicators() {
        this.effectIndicators = {};
        const indicators = this.container.querySelectorAll('.fx-indicator[data-effect]');
        indicators.forEach(el => {
            const effectKey = el.dataset.effect;
            this.effectIndicators[effectKey] = el;
        });
    }

    /**
     * Update FX activity indicator
     * @param {string} effectKey - Effect name (delay, reverb, chorus, etc.)
     * @param {boolean} active - Whether the effect is active (has sends > 0)
     */
    updateEffectIndicator(effectKey, active) {
        const indicator = this.effectIndicators[effectKey];
        if (indicator) {
            indicator.classList.toggle('active', active);
        }
    }

    /**
     * Register callback with AudioEngine to receive effect activity updates
     */
    registerEffectActivityCallback() {
        if (this.audioEngine && typeof this.audioEngine.onEffectActivity === 'function') {
            this.unregisterEffectActivity = this.audioEngine.onEffectActivity((effectKey, active) => {
                this.updateEffectIndicator(effectKey, active);
            });

            // Initialize indicators with current state
            const effectKeys = ['delay', 'reverb', 'chorus', 'phaser', 'flanger', 'crusher', 'tremolo', 'distortion'];
            effectKeys.forEach(key => {
                const active = this.audioEngine.isEffectActive(key);
                this.updateEffectIndicator(key, active);
            });
        }
    }

    createDelayControls() {
        const container = document.getElementById('delay_controls');

        // Time select (keep as regular select for now)
        const timeGroup = document.createElement('div');
        timeGroup.className = 'knob-group';
        timeGroup.innerHTML = `
            <select id="delayTime" class="time-select">
                <option value="4n">1/4</option>
                <option value="4n.">1/4.</option>
                <option value="8n" selected>1/8</option>
                <option value="8n.">1/8.</option>
                <option value="16n">1/16</option>
                <option value="8t">1/8T</option>
            </select>
            <span class="knob-label">Time</span>
        `;
        container.appendChild(timeGroup);

        document.getElementById('delayTime').addEventListener('change', (e) => {
            this.delay.setTime(e.target.value);
            this.store?.setSynthParam('effects', 'delayTime', e.target.value);
        });

        // Feedback
        this.components.knobs.delayFeedback = themeManager.createKnob(container, {
            min: 0, max: 0.9, value: this.delay.feedback,
            label: 'Feedback', unit: '%', step: 0.01
        }).onChange(value => {
            this.delay.setFeedback(value);
            this.store?.setSynthParam('effects', 'delayFeedback', value);
        });

        // Mix
        this.components.knobs.delayMix = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.delay.mix,
            label: 'Mix', unit: '%', step: 0.01
        }).onChange(value => {
            this.delay.setMix(value);
            this.store?.setSynthParam('effects', 'delayMix', value);
        });
    }

    createReverbControls() {
        const container = document.getElementById('reverb_controls');

        // Decay
        this.components.knobs.reverbDecay = themeManager.createKnob(container, {
            min: 0.5, max: 10, value: this.reverb.decay,
            label: 'Decay', unit: 's', step: 0.1, decimals: 1
        }).onChange(value => {
            this.reverb.setDecay(value);
            this.store?.setSynthParam('effects', 'reverbDecay', value);
        });

        // Pre-Delay
        this.components.knobs.reverbPreDelay = themeManager.createKnob(container, {
            min: 0, max: 0.1, value: this.reverb.preDelay,
            label: 'Pre-Dly', unit: 'ms', step: 0.001
        }).onChange(value => {
            this.reverb.setPreDelay(value);
            this.store?.setSynthParam('effects', 'reverbPreDelay', value);
        });

        // Mix
        this.components.knobs.reverbMix = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.reverb.mix,
            label: 'Mix', unit: '%', step: 0.01
        }).onChange(value => {
            this.reverb.setMix(value);
            this.store?.setSynthParam('effects', 'reverbMix', value);
        });
    }

    createChorusControls() {
        const container = document.getElementById('chorus_controls');

        // Rate (frequency)
        this.components.knobs.chorusRate = themeManager.createKnob(container, {
            min: 0.1, max: 10, value: this.chorus.frequency,
            label: 'Rate', unit: 'Hz', step: 0.1, decimals: 1
        }).onChange(value => {
            this.chorus.setFrequency(value);
            this.store?.setSynthParam('effects', 'chorusFrequency', value);
        });

        // Depth
        this.components.knobs.chorusDepth = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.chorus.depth,
            label: 'Depth', unit: '%', step: 0.01
        }).onChange(value => {
            this.chorus.setDepth(value);
            this.store?.setSynthParam('effects', 'chorusDepth', value);
        });

        // Mix
        this.components.knobs.chorusMix = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.chorus.mix,
            label: 'Mix', unit: '%', step: 0.01
        }).onChange(value => {
            this.chorus.setMix(value);
            this.store?.setSynthParam('effects', 'chorusMix', value);
        });
    }

    createPhaserControls() {
        const container = document.getElementById('phaser_controls');

        this.components.knobs.phaserRate = themeManager.createKnob(container, {
            min: 0.1, max: 8, value: this.phaser.frequency,
            label: 'Rate', unit: 'Hz', step: 0.1, decimals: 1
        }).onChange(value => {
            this.phaser.setFrequency(value);
            this.store?.setSynthParam('effects', 'phaserFrequency', value);
        });

        this.components.knobs.phaserQ = themeManager.createKnob(container, {
            min: 0.5, max: 20, value: this.phaser.q,
            label: 'Q', step: 0.5, decimals: 1
        }).onChange(value => {
            this.phaser.setQ(value);
            this.store?.setSynthParam('effects', 'phaserQ', value);
        });

        this.components.knobs.phaserMix = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.phaser.mix,
            label: 'Mix', unit: '%', step: 0.01
        }).onChange(value => {
            this.phaser.setMix(value);
            this.store?.setSynthParam('effects', 'phaserMix', value);
        });
    }

    createFlangerControls() {
        const container = document.getElementById('flanger_controls');

        this.components.knobs.flangerRate = themeManager.createKnob(container, {
            min: 0.05, max: 5, value: this.flanger.rate,
            label: 'Rate', unit: 'Hz', step: 0.05, decimals: 2
        }).onChange(value => {
            this.flanger.setRate(value);
            this.store?.setSynthParam('effects', 'flangerRate', value);
        });

        this.components.knobs.flangerFeedback = themeManager.createKnob(container, {
            min: 0, max: 0.95, value: this.flanger.feedback,
            label: 'Fdbk', unit: '%', step: 0.01
        }).onChange(value => {
            this.flanger.setFeedback(value);
            this.store?.setSynthParam('effects', 'flangerFeedback', value);
        });

        this.components.knobs.flangerMix = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.flanger.mix,
            label: 'Mix', unit: '%', step: 0.01
        }).onChange(value => {
            this.flanger.setMix(value);
            this.store?.setSynthParam('effects', 'flangerMix', value);
        });
    }

    createCrusherControls() {
        const container = document.getElementById('crusher_controls');

        this.components.knobs.crusherBits = themeManager.createKnob(container, {
            min: 2, max: 16, value: this.bitcrusher.bits,
            label: 'Bits', step: 1
        }).onChange(value => {
            this.bitcrusher.setBits(value);
            this.store?.setSynthParam('effects', 'crusherBits', value);
        });

        this.components.knobs.crusherMix = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.bitcrusher.mix,
            label: 'Mix', unit: '%', step: 0.01
        }).onChange(value => {
            this.bitcrusher.setMix(value);
            this.store?.setSynthParam('effects', 'crusherMix', value);
        });
    }

    createTremoloControls() {
        const container = document.getElementById('tremolo_controls');

        this.components.knobs.tremoloRate = themeManager.createKnob(container, {
            min: 0.5, max: 20, value: this.tremolo.frequency,
            label: 'Rate', unit: 'Hz', step: 0.5, decimals: 1
        }).onChange(value => {
            this.tremolo.setFrequency(value);
            this.store?.setSynthParam('effects', 'tremoloFrequency', value);
        });

        this.components.knobs.tremoloDepth = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.tremolo.depth,
            label: 'Depth', unit: '%', step: 0.01
        }).onChange(value => {
            this.tremolo.setDepth(value);
            this.store?.setSynthParam('effects', 'tremoloDepth', value);
        });

        this.components.knobs.tremoloMix = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.tremolo.mix,
            label: 'Mix', unit: '%', step: 0.01
        }).onChange(value => {
            this.tremolo.setMix(value);
            this.store?.setSynthParam('effects', 'tremoloMix', value);
        });
    }

    createDistortionControls() {
        const container = document.getElementById('distortion_controls');

        this.components.knobs.distAmount = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.distortion.amount,
            label: 'Drive', unit: '%', step: 0.01
        }).onChange(value => {
            this.distortion.setAmount(value);
            this.store?.setSynthParam('effects', 'distortionAmount', value);
        });

        this.components.knobs.distMix = themeManager.createKnob(container, {
            min: 0, max: 1, value: this.distortion.mix,
            label: 'Mix', unit: '%', step: 0.01
        }).onChange(value => {
            this.distortion.setMix(value);
            this.store?.setSynthParam('effects', 'distortionMix', value);
        });
    }

    createEQControls() {
        const container = document.getElementById('eq_controls');

        // Low
        this.components.knobs.eqLow = themeManager.createKnob(container, {
            min: -12, max: 12, value: this.eq.low,
            label: 'Low', unit: 'dB', step: 0.5, decimals: 1
        }).onChange(value => {
            this.eq.setLow(value);
            this.store?.setSynthParam('effects', 'eqLow', value);
        });

        // Mid
        this.components.knobs.eqMid = themeManager.createKnob(container, {
            min: -12, max: 12, value: this.eq.mid,
            label: 'Mid', unit: 'dB', step: 0.5, decimals: 1
        }).onChange(value => {
            this.eq.setMid(value);
            this.store?.setSynthParam('effects', 'eqMid', value);
        });

        // High
        this.components.knobs.eqHigh = themeManager.createKnob(container, {
            min: -12, max: 12, value: this.eq.high,
            label: 'High', unit: 'dB', step: 0.5, decimals: 1
        }).onChange(value => {
            this.eq.setHigh(value);
            this.store?.setSynthParam('effects', 'eqHigh', value);
        });
    }

    createCompressorControls() {
        const container = document.getElementById('comp_controls');

        // Threshold
        this.components.knobs.compThreshold = themeManager.createKnob(container, {
            min: -60, max: 0, value: this.compressor.threshold,
            label: 'Threshold', unit: 'dB', step: 1
        }).onChange(value => {
            this.compressor.setThreshold(value);
            this.store?.setSynthParam('effects', 'compThreshold', value);
        });

        // Ratio
        this.components.knobs.compRatio = themeManager.createKnob(container, {
            min: 1, max: 20, value: this.compressor.ratio,
            label: 'Ratio', step: 0.5
        }).onChange(value => {
            this.compressor.setRatio(value);
            this.store?.setSynthParam('effects', 'compRatio', value);
        });

        // Attack
        this.components.knobs.compAttack = themeManager.createKnob(container, {
            min: 0.001, max: 0.1, value: this.compressor.attack,
            label: 'Attack', unit: 'ms', step: 0.001
        }).onChange(value => {
            this.compressor.setAttack(value);
            this.store?.setSynthParam('effects', 'compAttack', value);
        });

        // Release
        this.components.knobs.compRelease = themeManager.createKnob(container, {
            min: 0.01, max: 1, value: this.compressor.release,
            label: 'Release', unit: 'ms', step: 0.01
        }).onChange(value => {
            this.compressor.setRelease(value);
            this.store?.setSynthParam('effects', 'compRelease', value);
        });

        // GR Meter
        const meterGroup = document.createElement('div');
        meterGroup.className = 'knob-group';
        meterGroup.innerHTML = `
            <div class="reduction-meter" id="reductionMeter">
                <div class="reduction-bar"></div>
                <span class="reduction-value">0 dB</span>
            </div>
            <span class="knob-label">GR</span>
        `;
        container.appendChild(meterGroup);
    }

    startGRMeter() {
        // Cache DOM references for performance
        this.grMeterBar = document.querySelector('.reduction-bar');
        this.grMeterValue = document.querySelector('.reduction-value');

        let lastUpdate = 0;
        const updateMeter = (time) => {
            if (!this.grEnabled) return;

            // Throttle updates based on performance profile
            if (time - lastUpdate >= this.grIntervalMs) {
                const reduction = this.compressor.getReduction();

                if (this.grMeterBar && this.grMeterValue) {
                    const reductionAbs = Math.abs(reduction);
                    const percent = Math.min(reductionAbs * 5, 100);
                    this.grMeterBar.style.height = percent + '%';
                    this.grMeterValue.textContent = reduction.toFixed(1) + ' dB';
                }

                lastUpdate = time;
            }

            this.grAnimationId = requestAnimationFrame(updateMeter);
        };

        this.grAnimationId = requestAnimationFrame(updateMeter);
    }

    stopGRMeter() {
        if (this.grAnimationId) {
            cancelAnimationFrame(this.grAnimationId);
        }
    }

    updateFromParams() {
        const { knobs } = this.components;
        if (knobs.delayFeedback) knobs.delayFeedback.setValue(this.delay.feedback);
        if (knobs.delayMix) knobs.delayMix.setValue(this.delay.mix);
        if (knobs.reverbDecay) knobs.reverbDecay.setValue(this.reverb.decay);
        if (knobs.reverbPreDelay) knobs.reverbPreDelay.setValue(this.reverb.preDelay);
        if (knobs.reverbMix) knobs.reverbMix.setValue(this.reverb.mix);
        if (knobs.compThreshold) knobs.compThreshold.setValue(this.compressor.threshold);
        if (knobs.compRatio) knobs.compRatio.setValue(this.compressor.ratio);
        if (knobs.compAttack) knobs.compAttack.setValue(this.compressor.attack);
        if (knobs.compRelease) knobs.compRelease.setValue(this.compressor.release);
    }

    destroy() {
        this.stopGRMeter();
        if (this.unregisterEffectActivity) {
            this.unregisterEffectActivity();
            this.unregisterEffectActivity = null;
        }
        this.effectIndicators = {};
        Object.values(this.components.knobs).forEach(knob => knob?.destroy());
    }
}
